
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Database, Plus, Key } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface KeyValuePair {
  id: string;
  key: string;
  value: string;
  description: string;
}

const AppSettings = () => {
  const [keyValuePairs, setKeyValuePairs] = useState<KeyValuePair[]>([
    { id: "1", key: "API_URL", value: "https://api.example.com", description: "Base URL for API calls" },
    { id: "2", key: "MAX_USERS", value: "100", description: "Maximum number of users allowed" },
    { id: "3", key: "DEFAULT_CURRENCY", value: "USD", description: "Default currency for all transactions" },
  ]);

  const [newKeyValuePair, setNewKeyValuePair] = useState<{
    key: string;
    value: string;
    description: string;
  }>({
    key: "",
    value: "",
    description: "",
  });

  const handleAddKeyValuePair = () => {
    if (!newKeyValuePair.key || !newKeyValuePair.value) {
      toast({
        title: "Error",
        description: "Key and Value are required fields.",
        variant: "destructive",
      });
      return;
    }

    const newPair: KeyValuePair = {
      id: Date.now().toString(),
      ...newKeyValuePair,
    };

    setKeyValuePairs([...keyValuePairs, newPair]);
    setNewKeyValuePair({ key: "", value: "", description: "" });
    
    toast({
      title: "Success",
      description: "Configuration added successfully",
    });
  };

  const handleAddIntegration = () => {
    toast({
      title: "Coming Soon",
      description: "Accounting system integration is coming soon!",
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Application Settings</h1>
        <p className="text-muted-foreground">
          Configure your application settings and integrations
        </p>
      </div>

      <div className="space-y-6">
        {/* Accounting System Integration Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Database className="mr-2 h-5 w-5" />
              Accounting System
            </CardTitle>
            <CardDescription>
              Integrate with external accounting systems to synchronize financial data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">No integrations configured</h3>
                <p className="text-sm text-muted-foreground">
                  Add an integration to connect with your accounting system
                </p>
              </div>
              <Button onClick={handleAddIntegration}>
                <Plus className="mr-2 h-4 w-4" />
                Add Integration
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Setup Key-Value Configuration Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Key className="mr-2 h-5 w-5" />
              Setup Configuration
            </CardTitle>
            <CardDescription>
              Manage application configuration using key-value pairs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Table>
              <TableCaption>
                Application configuration key-value pairs
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keyValuePairs.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.key}</TableCell>
                    <TableCell>{item.value}</TableCell>
                    <TableCell>{item.description}</TableCell>
                  </TableRow>
                ))}
                {/* Empty rows to complete 6 rows total */}
                {Array.from({ length: Math.max(0, 6 - keyValuePairs.length) }).map((_, index) => (
                  <TableRow key={`empty-${index}`}>
                    <TableCell className="h-12"></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Separator />

            <div className="grid grid-cols-1 gap-4 pt-4">
              <h3 className="text-lg font-medium">Add New Configuration</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="key">Key</Label>
                  <Input
                    id="key"
                    value={newKeyValuePair.key}
                    onChange={(e) =>
                      setNewKeyValuePair({ ...newKeyValuePair, key: e.target.value })
                    }
                    placeholder="API_KEY"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="value">Value</Label>
                  <Input
                    id="value"
                    value={newKeyValuePair.value}
                    onChange={(e) =>
                      setNewKeyValuePair({ ...newKeyValuePair, value: e.target.value })
                    }
                    placeholder="your-value-here"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={newKeyValuePair.description}
                    onChange={(e) =>
                      setNewKeyValuePair({ ...newKeyValuePair, description: e.target.value })
                    }
                    placeholder="What this configuration is used for"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={handleAddKeyValuePair}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Configuration
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppSettings;
